import { useState, useEffect } from "react";
import { useUser, useUpdateUser } from "./api/userHooks";
import { useAuthStore } from "@/lib/store/authStore";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { timezones } from "@/lib/timezones";
import { UserCircle, Check } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import type { User } from "./types";
import { logger } from "@/lib/utils/logger";

interface UserProfileEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section: "basic" | "more";
}

export function UserProfileEditModal({
  open,
  onOpenChange,
  section,
}: UserProfileEditModalProps) {
  const username = useAuthStore((state) => state.username);
  const queryClient = useQueryClient();

  const { data: userData } = useUser(username || "");
  const updateUser = useUpdateUser(username || "");

  const [formData, setFormData] = useState<Partial<User>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Lock/unlock scroll based on modal open state
  useEffect(() => {
    if (open) {
      // Lock scroll when modal opens
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    } else {
      // Unlock scroll when modal closes
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  }, [open]);

  // Load form data immediately when modal opens
  useEffect(() => {
    if (userData && open) {
      setFormData({
        email: userData.email || "",
        full_name: userData.full_name || "",
        first_name: userData.first_name || "",
  // @ts-expect-error - Property may not exist on type
        middle_name: userData.middle_name || "",
        last_name: userData.last_name || "",
        user_type: userData.user_type || "System User",
        phone: userData.phone || "",
        mobile_no: userData.mobile_no || "",
  // @ts-expect-error - Property may not exist on type
        gender: userData.gender || "",
  // @ts-expect-error - Property may not exist on type
        birth_date: userData.birth_date || "",
  // @ts-expect-error - Property may not exist on type
        location: userData.location || "",
        language: userData.language || "",
        time_zone: userData.time_zone || "",
  // @ts-expect-error - Property may not exist on type
        bio: userData.bio || "",
        // @ts-expect-error - Property may not exist on type
        interest: userData.interest || "",
      });
    }
  }, [userData, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await updateUser.mutateAsync(formData);
      queryClient.invalidateQueries({ queryKey: ["get", "User", username] });
      onOpenChange(false);
    } catch (error) {
      logger.error("Error saving profile:", error);
      alert("Failed to save profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const renderBasicFields = () => (
    <div className="py-2">
      <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40 last:border-b-0">
          <Label htmlFor="email" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
          Email
        </Label>
        <Input
          id="email"
          type="email"
            value={formData.email || ""}
          disabled
            className="flex-1 max-w-[60%] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-500 dark:text-gray-400 font-normal focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:!bg-transparent hover:!bg-transparent hover:shadow-none transition-none rounded-none cursor-not-allowed shadow-none"
        />
      </div>
        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40 last:border-b-0">
          <Label htmlFor="full_name" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
          Full Name
        </Label>
        <Input
          id="full_name"
            value={formData.full_name || ""}
          onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            className="flex-1 max-w-[60%] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:!bg-transparent hover:!bg-transparent hover:shadow-none transition-none rounded-none placeholder:text-gray-400 shadow-none"
            placeholder=""
        />
      </div>
        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40 last:border-b-0">
          <Label htmlFor="first_name" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
            First Name
          </Label>
          <Input
            id="first_name"
            value={formData.first_name || ""}
            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            className="flex-1 max-w-[60%] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:!bg-transparent hover:!bg-transparent hover:shadow-none transition-none rounded-none placeholder:text-gray-400 shadow-none"
            placeholder=""
          />
        </div>
        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40 last:border-b-0">
          <Label htmlFor="middle_name" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
            Middle Name
          </Label>
          <Input
            id="middle_name"
            value={formData.middle_name || ""}
            onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
            className="flex-1 max-w-[60%] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:!bg-transparent hover:!bg-transparent hover:shadow-none transition-none rounded-none placeholder:text-gray-400 shadow-none"
            placeholder=""
          />
        </div>
        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40 last:border-b-0">
          <Label htmlFor="last_name" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
          Last Name
        </Label>
        <Input
          id="last_name"
            value={formData.last_name || ""}
          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            className="flex-1 max-w-[60%] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:!bg-transparent hover:!bg-transparent hover:shadow-none transition-none rounded-none placeholder:text-gray-400 shadow-none"
            placeholder=""
        />
      </div>
        <div className="flex items-center justify-between min-h-[52px] px-5">
          <Label htmlFor="user_type" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
          User Type
        </Label>
        <Input
          id="user_type"
            value={formData.user_type || ""}
          onChange={(e) => setFormData({ ...formData, user_type: e.target.value })}
            className="flex-1 max-w-[60%] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:!bg-transparent hover:!bg-transparent hover:shadow-none transition-none rounded-none placeholder:text-gray-400 shadow-none"
            placeholder=""
        />
        </div>
      </div>
    </div>
  );

  const renderMoreFields = () => (
    <div className="py-2 space-y-5">
      <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40 last:border-b-0">
          <Label htmlFor="phone" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
            Phone
          </Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone || ""}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="flex-1 max-w-[60%] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:!bg-transparent hover:!bg-transparent hover:shadow-none transition-none rounded-none placeholder:text-gray-400 shadow-none"
            placeholder=""
          />
        </div>
        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40 last:border-b-0">
          <Label htmlFor="mobile_no" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
            Mobile Number
          </Label>
          <Input
            id="mobile_no"
            type="tel"
            value={formData.mobile_no || ""}
            onChange={(e) => setFormData({ ...formData, mobile_no: e.target.value })}
            className="flex-1 max-w-[60%] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:!bg-transparent hover:!bg-transparent hover:shadow-none transition-none rounded-none placeholder:text-gray-400 shadow-none"
            placeholder=""
          />
        </div>
        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40 last:border-b-0">
          <Label htmlFor="gender" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
            Gender
          </Label>
          <Select
            value={formData.gender || ""}
            onValueChange={(value) => setFormData({ ...formData, gender: value })}
          >
            <SelectTrigger className="flex-1 max-w-[60%] text-right justify-end pr-2">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Male">Male</SelectItem>
              <SelectItem value="Female">Female</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40 last:border-b-0">
          <Label htmlFor="birth_date" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
            Birth Date
          </Label>
          <div className="flex-1 max-w-[60%] text-right">
            <DatePicker
            value={formData.birth_date}
              onChange={(value) => setFormData({ ...formData, birth_date: value })}
              placeholder="Select date"
              className="w-full"
          />
        </div>
      </div>
        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40 last:border-b-0">
          <Label htmlFor="location" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
            Location
          </Label>
          <Input
            id="location"
            value={formData.location || ""}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="flex-1 max-w-[60%] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:!bg-transparent hover:!bg-transparent hover:shadow-none transition-none rounded-none placeholder:text-gray-400 shadow-none"
            placeholder=""
          />
        </div>
        <div className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40 last:border-b-0">
          <Label htmlFor="language" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
            Language
          </Label>
          <Input
            id="language"
            value={formData.language || ""}
            onChange={(e) => setFormData({ ...formData, language: e.target.value })}
            className="flex-1 max-w-[60%] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:!bg-transparent hover:!bg-transparent hover:shadow-none transition-none rounded-none placeholder:text-gray-400 shadow-none"
            placeholder=""
          />
        </div>
        <div className="flex items-center justify-between min-h-[52px] px-5">
          <Label htmlFor="time_zone" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
            Time Zone
          </Label>
          <Select
            value={formData.time_zone || ""}
            onValueChange={(value) => setFormData({ ...formData, time_zone: value })}
          >
            <SelectTrigger className="flex-1 max-w-[60%] text-right justify-end pr-2">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {timezones.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
        <div className="px-5 py-4 border-b border-gray-200/40 dark:border-gray-700/40">
          <Label htmlFor="bio" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal">
          Bio
        </Label>
        </div>
        <div className="px-5 py-4">
        <Textarea
          id="bio"
            value={formData.bio || ""}
          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            rows={4}
            className="w-full !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:!bg-transparent hover:!bg-transparent hover:shadow-none transition-none rounded-none resize-none placeholder:text-gray-400 leading-relaxed shadow-none"
            placeholder=""
        />
        </div>
      </div>
      
      <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
        <div className="px-4 py-3 border-b border-gray-200/40 dark:border-gray-700/40">
          <Label htmlFor="interest" className="text-[17px] text-gray-900 dark:text-gray-100 font-normal">
          Interests
        </Label>
        </div>
        <div className="px-4 py-3">
        <Textarea
          id="interest"
            value={formData.interest || ""}
          onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
            rows={3}
            className="w-full !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:!bg-transparent hover:!bg-transparent hover:shadow-none transition-none rounded-none resize-none placeholder:text-gray-400 leading-relaxed shadow-none"
            placeholder=""
        />
        </div>
      </div>
    </div>
  );

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[680px] max-h-[85vh] bg-white dark:bg-gray-900 p-0 flex flex-col overflow-hidden">
        {/* Top fade-out gradient - removed backdrop-blur for performance */}
        <div className="absolute top-0 left-0 right-0 z-30 h-12 bg-gradient-to-b from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
        
        {/* Checkmark button - top right */}
        <Button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            handleSubmit(e as any);
          }}
          disabled={isSaving}
          className={cn(
            "absolute right-4 top-4 z-50 rounded-full bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] shadow-apple-sm",
            "focus:outline-none focus:ring-0",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          size="icon"
        >
          <Check className="w-5 h-5 text-white" />
          <span className="sr-only">Save</span>
        </Button>
        
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
            <DialogHeader className="px-10 pt-20 pb-4 text-center">
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-blue-500/10 p-3">
                  <UserCircle className="h-8 w-8 text-blue-600 dark:text-blue-500" />
                </div>
              </div>
              <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px] mb-2">
                {section === "basic" ? "Basic Information" : "More Information"}
          </DialogTitle>
              <DialogDescription className="text-[15px] font-normal text-gray-600 dark:text-gray-400 leading-relaxed">
                Update your profile information below
          </DialogDescription>
        </DialogHeader>
            <div className="pb-8 px-10 pt-2">
            {section === "basic" ? renderBasicFields() : renderMoreFields()}
            </div>
            
            {/* Bottom fade-out gradient - removed backdrop-blur for performance */}
            <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
          </div>
          
          {/* Sticky Save Button */}
          <div className="flex justify-center px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
            <Button
              type="submit" 
              disabled={isSaving}
              className="w-full text-[17px] font-semibold bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] text-white px-7 py-3.5 h-auto rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

